'use client';

import React, { useMemo } from 'react';
import { X, Stethoscope, PlusCircle } from 'lucide-react';
import { AiSkillPublic, AI_SKILL_CATEGORY_LABELS, AiSkillCategory } from '@ctrl-enter/types/ai-skills';
import { cn } from '../../utils/cn';
import { Dialog } from '../dialog';
import { SkillCard } from './skill-card';

const CATEGORY_ORDER: Partial<Record<AiSkillCategory, number>> = {
  integration: 0, // EMR Integrations always first
};

type SkillSelectorModalProps = {
  isOpen: boolean;
  onClose: () => void;
  skills: AiSkillPublic[];
  selectedSkillId: string | null;
  onSelectSkill: (skillId: string | null) => void;
  onRequestNewEmr?: () => void;
};

export const SkillSelectorModal: React.FC<SkillSelectorModalProps> = ({
  isOpen,
  onClose,
  skills,
  selectedSkillId,
  onSelectSkill,
  onRequestNewEmr,
}) => {
  const grouped = useMemo(() => {
    const map = new Map<AiSkillCategory, AiSkillPublic[]>();
    for (const skill of skills) {
      const existing = map.get(skill.category) ?? [];
      map.set(skill.category, [...existing, skill]);
    }
    return map;
  }, [skills]);

  const sortedEntries = useMemo(() => {
    return Array.from(grouped.entries()).sort(([a], [b]) => {
      const orderA = CATEGORY_ORDER[a] ?? 999;
      const orderB = CATEGORY_ORDER[b] ?? 999;
      return orderA - orderB;
    });
  }, [grouped]);

  const handleSelect = (skill: AiSkillPublic) => {
    if (skill.id === selectedSkillId) {
      // Toggle off if already selected
      onSelectSkill(null);
    } else {
      onSelectSkill(skill.id);
    }
    onClose();
  };

  const handleClear = () => {
    onSelectSkill(null);
    onClose();
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} size="lg" aria-labelledby="skill-selector-title">
      <div className="flex flex-col h-full max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-border/30 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary/10 text-primary">
              <Stethoscope size={18} />
            </div>
            <div>
              <h2 id="skill-selector-title" className="text-base font-semibold text-foreground">
                AI Specialists
              </h2>
              <p className="text-xs text-text-secondary mt-0.5">
                Choose a clinical specialist to focus the AI on a specific domain
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex items-center justify-center w-8 h-8 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-bg-neutral-7 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Skill grid */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {skills.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Stethoscope size={40} className="text-text-tertiary mb-3" />
              <p className="text-sm text-text-secondary">No specialists available</p>
            </div>
          )}

          {sortedEntries.map(([category, categorySkills]) => (
            <div key={category}>
              <h3 className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-3">
                {AI_SKILL_CATEGORY_LABELS[category]}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {categorySkills.map(skill => (
                  <SkillCard
                    key={skill.id}
                    skill={skill}
                    isSelected={skill.id === selectedSkillId}
                    onSelect={handleSelect}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          className={cn(
            'flex items-center justify-between px-6 py-4 border-t border-border/30 flex-shrink-0'
          )}
        >
          <button
            type="button"
            onClick={() => {
              onRequestNewEmr?.();
              onClose();
            }}
            className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-primary transition-colors"
          >
            <PlusCircle size={13} />
            Request new EMR
          </button>
          <div className="flex items-center gap-3">
            {selectedSkillId && (
              <button
                type="button"
                onClick={handleClear}
                className="text-xs text-text-secondary hover:text-text-primary transition-colors"
              >
                Clear specialist
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-xs px-4 py-1.5 rounded-lg bg-bg-neutral-7 hover:bg-bg-neutral-6 text-text-secondary hover:text-text-primary transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
